import { authHeaders, resolveUrl } from '../api';
import type { QVoiceLang } from './qVoice';

/**
 * Klienten mot Q-motorn (Q-1, docs/q-api.md): POST /api/q/messages med svaret som SSE, läst med fetch så att
 * token inte hamnar i URL:en. Konversationen ligger hos klienten (localStorage per person och hem) och
 * skickas hela varje gång. Här finns också tolkningen av ett skrivet eller talat "ja"/"nej" och
 * inställningarna. Ingenting här ritar något.
 */

// ---------- Konversationen (Anthropics format) ----------

export type QBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: unknown; is_error?: boolean }
  | { type: string; [k: string]: unknown };

export interface QMessage {
  role: 'user' | 'assistant';
  content: string | QBlock[];
}

export interface QPending {
  id: string;
  name: string;
  input: Record<string, unknown>;
  level: number;
  requiresTap: boolean;
  description: string;
  /** Appen som anropas (Q-6), annars Qvenari. */
  app?: QAppId;
}

export interface QToolEvent {
  id: string;
  name: string;
  level: number;
  text: string;
  status: 'running' | 'done' | 'error';
  app?: QAppId;
}

export interface QDone {
  status: 'done' | 'confirm_required' | 'error';
  messages: QMessage[];
  text?: string;
  pending?: QPending[];
  error?: string;
}

export interface QStreamHandlers {
  start?: (usage: { used: number; limit: number | null }) => void;
  text?: (sentence: string) => void;
  tool?: (e: QToolEvent) => void;
  confirm?: (pending: QPending[]) => void;
}

/** Det appen vet just nu (API:t: q/engine.ts QContext): position, pågående jakt och senaste kamerabild. */
export interface QContext {
  /** heading: kompassriktning i grader från norr ("till höger" för Q, QA1). */
  position?: { lat: number; lng: number; accuracy?: number; at?: string; heading?: number };
  hunt?: { markId: string; markName?: string; title?: string; startedAt?: string };
  imageUrl?: string;
}

export interface QRequest {
  /** Position, jakt och bild (Qvenari). */
  context?: QContext;
  homeId: string;
  messages: QMessage[];
  action?: 'confirm' | 'cancel';
  tap?: boolean;
  /** Ett startkort: appens prompt läggs efter frågan (Q-6). */
  prompt?: { app: QAppId; name: string };
}

/** Fel före strömmen (400, 401, 403, 409, 429, 503) med serverns text. */
export class QHttpError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

/** Delar SSE-text i händelser. Returnerar det som ännu inte är en hel händelse. */
export function parseSse(buffer: string, onEvent: (event: string, data: unknown) => void): string {
  const parts = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    let event = 'message';
    const data: string[] = [];
    for (const line of part.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    if (!data.length) continue;
    try { onEvent(event, JSON.parse(data.join('\n'))); } catch { /* trasig rad hoppas över */ }
  }
  return rest;
}

/** Skickar till Q och läser strömmen till slut. `done` kommer alltid sist. */
export async function streamQ(req: QRequest, on: QStreamHandlers, signal?: AbortSignal, fetchImpl: typeof fetch = fetch, headers: Record<string, string> = authHeaders()): Promise<QDone> {
  const res = await fetchImpl(resolveUrl('/api/q/messages'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...headers },
    body: JSON.stringify(req),
    signal,
  });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new QHttpError(res.status, data.error || `HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done: QDone | null = null;
  const handle = (event: string, data: any) => {
    if (event === 'start') on.start?.(data);
    else if (event === 'text') on.text?.(data.text);
    else if (event === 'tool') on.tool?.(data);
    else if (event === 'confirm') on.confirm?.(data.pending);
    else if (event === 'done') done = data;
  };
  for (;;) {
    const { value, done: end } = await reader.read();
    if (value) buffer = parseSse(buffer + decoder.decode(value, { stream: true }), handle);
    if (end) break;
  }
  if (buffer.trim()) parseSse(buffer + '\n\n', handle);
  if (!done) throw new Error('Svaret från Q tog slut i förtid.');
  return done;
}

export async function fetchQStatus(homeId: string): Promise<{ mode: 'live' | 'mock' | 'off'; model: string; used: number; limit: number | null } | null> {
  try {
    const res = await fetch(resolveUrl(`/api/q/status?homeId=${encodeURIComponent(homeId)}`), { headers: authHeaders() });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

// ---------- Andra appar (Q-6) ----------

export type QAppId = 'qvenari' | 'qvestium';

export interface QAppPrompt { app: QAppId; name: string; title: string; description?: string }

/** on = påslagen; off = av (konto okänt tills den slås på); no_account = switchen döljs; unavailable = svarar inte. */
export interface QRemoteApp { id: QAppId; name: string; status: 'on' | 'off' | 'no_account' | 'unavailable'; tools?: number; prompts?: QAppPrompt[] }

export const APP_NAMES: Record<QAppId, string> = { qvenari: 'Qvenari', qvestium: 'Qvestium' };

/** Apparnas läge, eller null om det inte gick att fråga. */
export async function fetchQApps(fetchImpl: typeof fetch = fetch, headers?: Record<string, string>): Promise<QRemoteApp[] | null> {
  try {
    const res = await fetchImpl(resolveUrl('/api/q/apps'), { headers: headers ?? authHeaders() });
    return res.ok ? ((await res.json()) as { apps: QRemoteApp[] }).apps : null;
  } catch {
    return null;
  }
}

/** Switchen: slår på (samtycke i Qid och kontroll av kontot) eller av. */
export async function setQApp(id: QAppId, on: boolean, fetchImpl: typeof fetch = fetch, headers: Record<string, string> = authHeaders()): Promise<QRemoteApp> {
  const res = await fetchImpl(resolveUrl(`/api/q/apps/${id}`), { method: on ? 'PUT' : 'DELETE', headers });
  const data = await res.json().catch(() => ({})) as { app?: QRemoteApp; error?: string };
  if (!res.ok || !data.app) throw new QHttpError(res.status, data.error || `HTTP ${res.status}`);
  return data.app;
}

// ---------- Visning ----------

/** Startkortets instruktioner från appen (lagt av servern), som inte visas. */
export const isPromptText = (text: string) => text.startsWith('<q-prompt ');

export function textOf(content: string | QBlock[]): string {
  if (typeof content === 'string') return content;
  return content.filter(b => b.type === 'text').map(b => (b as { text: string }).text).filter(t => !isPromptText(t)).join('\n');
}

export function toolUsesOf(content: string | QBlock[]): { id: string; name: string }[] {
  if (typeof content === 'string') return [];
  return content.filter(b => b.type === 'tool_use').map(b => { const u = b as { id: string; name: string }; return { id: String(u.id), name: String(u.name) }; });
}

/** Ett användarmeddelande som bara bär verktygssvar visas inte. */
export function isToolResultMessage(m: QMessage): boolean {
  return m.role === 'user' && typeof m.content !== 'string' && m.content.every(b => b.type === 'tool_result');
}

export const IMAGE_PLACEHOLDER = '[bild]';

/**
 * Bilder skickas en gång och byts sedan mot en platshållare (som withoutImages i Q work), så att
 * historiken inte växer och localStorage räcker.
 */
export function withoutImages(messages: QMessage[]): QMessage[] {
  return messages.map(m => {
    if (typeof m.content === 'string' || !m.content.some(b => b.type === 'image')) return m;
    return { ...m, content: m.content.map(b => (b.type === 'image' ? { type: 'text', text: IMAGE_PLACEHOLDER } : b)) };
  });
}

/** Skalar ner en bild till en JPEG (base64) som räcker för Claude. */
export async function imageToBlock(file: File, max = 1568): Promise<{ block: QBlock; preview: string }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Bilden gick inte att läsa'));
      el.src = url;
    });
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const preview = canvas.toDataURL('image/jpeg', 0.85);
    return { block: { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: preview.split(',')[1] } }, preview };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------- Ja och nej ----------

const norm = (text: string) => text.toLowerCase().replace(/[.,!?;:"']/g, ' ').replace(/\s+/g, ' ').trim();

/** Ett skrivet eller talat ja ("ja", "ja tack", "gör det", "yes please", "okej"). */
export function isYes(text: string): boolean {
  return /^(ja|japp|jajamän|jo|absolut|visst|okej|ok|kör|gör det|gärna|yes|yeah|yep|sure|okay|do it|go ahead|please do)( (tack|gärna|det|please|thanks|thank you|kör|gör det|do it))*$/.test(norm(text));
}

/** Ett skrivet eller talat nej ("nej", "nej tack", "avbryt", "no", "cancel"). */
export function isNo(text: string): boolean {
  return /^(nej|nä|nää|avbryt|låt bli|strunta i det|no|nope|cancel|don't|do not|stop)( (tack|det|thanks|thank you|do it))*$/.test(norm(text));
}

// ---------- Språk och uppläsning ----------

const SV_WORDS = /\b(och|är|jag|det|att|på|inte|som|har|för|med|en|ett|vad|vem|hur|ska|kan|du|hemma|listan|tack|hej|ja|nej)\b/g;
const EN_WORDS = /\b(and|is|the|it|to|on|not|that|have|for|with|a|an|what|who|how|shall|can|you|home|list|thanks|hi|yes|no|are|i)\b/g;

/** Gissar om en text är svenska eller engelska (för uppläsningen och lyssningen i Auto). */
export function guessLang(text: string, fallback: QVoiceLang): QVoiceLang {
  const t = text.toLowerCase();
  if (/[åäö]/.test(t)) return 'sv-SE';
  const sv = t.match(SV_WORDS)?.length ?? 0;
  const en = t.match(EN_WORDS)?.length ?? 0;
  if (sv === en) return fallback;
  return sv > en ? 'sv-SE' : 'en-US';
}

/** Text att läsa upp: utan Markdown-tecken, länkar blir sin text. */
export function speechText(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`#>|]/g, '')
    .replace(/^\s*([-•]|\d+[.)])\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ---------- Inställningar och historik ----------

export type QLangSetting = 'sv-SE' | 'en-US' | 'auto';

export interface QSettings {
  lang: QLangSetting;
  voices: Partial<Record<QVoiceLang, string>>;
  /** Röstalternativet (CV1): Apple Lokal (standard), Anthropic + ElevenLabs, eller ChatGPT (OpenAI Realtime, chatgptVoice.ts). */
  engine: 'apple' | 'elevenlabs' | 'openai';
  /** ChatGPT-rösten: Qvenaris OpenAI-konto eller personens egen nyckel (CV2). */
  openaiAccount: 'qvenari' | 'own';
  /** ChatGPT-rösten i iPhone-appen: ljudet i appen så att samtalet fortsätter med låst skärm (CV4b). Av = WebRTC i webbvyn. Saknas = på. */
  openaiBackground?: boolean;
  elevenVoices: Partial<Record<QVoiceLang, string>>;
  conversation: boolean;
  readAloud: boolean;
}

/** Q:s samtal sparas per person (localStorage); Qvenari har ett samtal (toppradens panel och den under jakt), inte ett per hem som Qdomus. */
export const Q_CONVERSATION = { id: 'qvenari' };

export const DEFAULT_SETTINGS: QSettings = { lang: 'auto', voices: {}, engine: 'apple', openaiAccount: 'qvenari', elevenVoices: {}, conversation: false, readAloud: false };

const settingsKey = (sub: string) => `qvenari_q_settings_${sub}`;
const historyPrefix = (sub: string) => `qvenari_q_history_${sub}_`;
const historyKey = (sub: string, homeId: string) => `${historyPrefix(sub)}${homeId}`;

export interface QHistory {
  messages: QMessage[];
  /** Verktygskortens text per tool_use-id, så att historiken visar "Hämtade inköpslistan" och inte namnet. */
  tools: Record<string, { text: string; status: QToolEvent['status']; app?: QAppId }>;
  /** Kvitton på bekräftelser: tool_use-id → godkänt eller avböjt. */
  decisions: Record<string, 'confirm' | 'cancel'>;
  pending: QPending[] | null;
}

export const EMPTY_HISTORY: QHistory = { messages: [], tools: {}, decisions: {}, pending: null };

function read<T>(key: string): T | null {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : null; } catch { return null; }
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* fullt eller privat läge */ }
}

export function loadSettings(sub: string): QSettings {
  return { ...DEFAULT_SETTINGS, ...read<Partial<QSettings>>(settingsKey(sub)) };
}
/** Skickas när Q:s inställningar sparas, så att andra vyer (UX10: Integrationer) visar samma val. */
export const Q_SETTINGS_EVENT = 'qvenari-q-settings';
export function saveSettings(sub: string, s: QSettings) {
  write(settingsKey(sub), s);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(Q_SETTINGS_EVENT, { detail: sub }));
}

/** Högst så här många meddelanden sparas (de äldsta faller bort, från ett nytt användarmeddelande). */
export const MAX_STORED_MESSAGES = 120;

export function trimHistory(messages: QMessage[], max = MAX_STORED_MESSAGES): QMessage[] {
  if (messages.length <= max) return messages;
  let start = messages.length - max;
  // Börja på en riktig fråga, så att inget verktygssvar blir utan sitt anrop.
  while (start < messages.length && (messages[start].role !== 'user' || isToolResultMessage(messages[start]))) start++;
  return messages.slice(start);
}

export function loadHistory(sub: string, homeId: string): QHistory {
  const h = read<QHistory>(historyKey(sub, homeId));
  return h && Array.isArray(h.messages) ? { ...EMPTY_HISTORY, ...h } : EMPTY_HISTORY;
}
export function saveHistory(sub: string, homeId: string, h: QHistory) {
  const messages = trimHistory(withoutImages(h.messages));
  const ids = new Set(messages.flatMap(m => toolUsesOf(m.content).map(t => t.id)));
  const keep = <V,>(r: Record<string, V>) => Object.fromEntries(Object.entries(r).filter(([id]) => ids.has(id)));
  write(historyKey(sub, homeId), { messages, tools: keep(h.tools), decisions: keep(h.decisions), pending: h.pending });
}

/** "Rensa historik": alla hem för personen på den här enheten. */
export function clearAllHistory(sub: string) {
  try {
    const prefix = historyPrefix(sub);
    for (const key of Object.keys(localStorage)) if (key.startsWith(prefix)) localStorage.removeItem(key);
  } catch { /* privat läge */ }
}
